import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import size from 'lodash/size';
import isArray from 'lodash/isArray';
import { QUERY_KEYS } from '../../config/constants';
import usePersonStore from '../../store/personStore';
import {
  addDriverFn,
  addNewDriversFn,
  assignDriverFn,
  deleteDriverFn,
  editDriversFn,
  getDriverFn,
  getDriversByPlateNumberFn,
  getDriversFn,
  getDriversForCarFn,
  getUploadedDriverDetailsFn,
  getUploadedDriverListFn,
  unassignDriverFn,
  updatePrimaryDriverFn,
  uploadDriverFn,
} from '../../transactions/drivers/drivers';
import { getExistingDrivers } from '../../transactions/download/download';
import { queryClient } from '../../utils/queryClient';
import { toast } from '../../components/ui/Toast/useToast';

const {
  DRIVERS,
  EXPORT_DRIVERS,
  CREATE_DRIVER,
  MULTIPLE_UPLOAD_LIST,
  MULTIPLE_UPLOAD_REPORTS,
  ASSIGN_DRIVERS,
} = QUERY_KEYS;

const useDriversData = () => {
  const { setDriverList } = usePersonStore();

  return useQuery({
    queryKey: [DRIVERS],
    queryFn: getDriversFn,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      setDriverList(data);
      // navigate('/finalize_offer');
    },
  });
};

export const useDriversListForCar = (plateNumber) => {
  return useQuery({
    queryKey: [ASSIGN_DRIVERS, plateNumber],
    queryFn: () => getDriversForCarFn(plateNumber),
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      // navigate('/finalize_offer');
    },
  });
};

export const useAddDriver = () => {
  return useMutation({
    mutationFn: (payload) => addNewDriversFn(payload),
    onSuccess: (data) => {
      if (data?.data?.driverDetails) {
        queryClient.invalidateQueries({ queryKey: [DRIVERS] });
      }
    },
  });
};

export const useEditDriver = () => {
  return useMutation({
    mutationFn: ({ driverId, payload }) => editDriversFn(driverId, payload),
    onSuccess: (data) => {
      if (data?.data === 'success') {
        queryClient.invalidateQueries({ queryKey: [DRIVERS] });
      }
    },
    onError: () => {
      toast({
        variant: 'destructive',
        description: 'Παρουσιάστηκε κάποιο πρόβλημα',
      });
    },
  });
};

export const useSetPrimaryDriver = () => {
  return useMutation({
    mutationFn: (payload) => updatePrimaryDriverFn(payload),
    onSuccess: (data) => {
      console.log('useSetPrimaryDriver', data);
    },
  });
};

export const useAssignDrivers = () => {
  return useMutation({
    mutationFn: (payload) => assignDriverFn(payload),
    onSuccess: (data) => {
    },
    onError: () => {
      toast({
        variant: 'destructive',
        description: 'Παρουσιάστηκε κάποιο πρόβλημα',
      });
    },
  });
};

export const useUnAssignDrivers = () => {
  return useMutation({
    mutationFn: (payload) => unassignDriverFn(payload),
    onSuccess: (data) => {
    },
  });
};

export const useDeleteDrivers = () => {
  return useMutation({
    mutationFn: (driverId) => deleteDriverFn(driverId),
    onSuccess: (response) => {
      if (response?.data === 'success') {
        queryClient.invalidateQueries({ queryKey: [DRIVERS] });
      } else if (response?.data === 'fail') {
        if (response.exceptions) {
          if (size(response.exceptions) > 0) {
            const exceptionsArray = isArray(response.exceptions);
            const message = exceptionsArray
              ? response.exceptions.slice(-1)
              : 'Παρουσιάστηκε κάποιο πρόβλημα';
            toast({
              variant: 'destructive',
              description: message || 'Παρουσιάστηκε κάποιο πρόβλημα',
            });
          }
        } else {
          toast({
            variant: 'destructive',
            description: 'Παρουσιάστηκε κάποιο πρόβλημα',
          });
        }
      }
    },
  });
};

export const useGetDriverForm = () => {

  return useQuery({
    queryKey: [CREATE_DRIVER],
    queryFn: addDriverFn,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

export const useGetDriverInformation = (driverId) => {
  return useQuery({
    queryKey: [DRIVERS, driverId],
    queryFn: () => getDriverFn(driverId),
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      // setDriverList(data);
      // navigate('/finalize_offer');
    },
  });
};

export const useDriversByPlateNumber = (platNumber, fetchData = true) => {
  return useQuery({
    queryKey: [DRIVERS, platNumber],
    queryFn: () => getDriversByPlateNumberFn(platNumber),
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: !!fetchData,
    onSuccess: (data) => {
      // setDriverList(data);
      // navigate('/finalize_offer');
    },
  });
};

export const useUploadedDriverList = () => {
  return useQuery({
    queryKey: [MULTIPLE_UPLOAD_LIST],
    queryFn: () => getUploadedDriverListFn(),
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      // setDriverList(data);
      // navigate('/finalize_offer');
    },
  });
};

export const useUploadDriverDetailsList = ({ id, count }) => {
  return useInfiniteQuery({
    queryKey: [MULTIPLE_UPLOAD_REPORTS, id, count],
    queryFn: ({ pageParam = 1 }) =>
      getUploadedDriverDetailsFn({ id, count, pageParam }),
    getNextPageParam: (lastPage, pages) => {
      return pages.length + 1;
    },
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: !!id,
    onSuccess: (data) => {
      // setDriverList(data);
      // navigate('/finalize_offer');
    },
  });
};

export const useUploadDriver = () => {
  return useMutation({
    mutationFn: (payload) => uploadDriverFn(payload),
    onError: () => {
      toast({
        variant: 'destructive',
        description: 'Παρουσιάστηκε κάποιο πρόβλημα',
      });
    },
  });
};

export const useExportDrivers = () => {
  return useMutation({
    mutationFn: () => getExistingDrivers(),
    onSuccess: (response) => {
      if (response.exceptions) {
        if (size(response.exceptions) > 0) {
          const exceptionsArray = isArray(response.exceptions);
          const message = exceptionsArray
            ? response.exceptions.slice(-1)
            : 'Παρουσιάστηκε κάποιο πρόβλημα';
          toast({
            variant: 'destructive',
            description: message || 'Παρουσιάστηκε κάποιο πρόβλημα',
          });
        }
      }
      const url = window.URL.createObjectURL(
        new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'exportDrivers.xlsx');
      document.body.appendChild(link);
      link.click();
    },
    onError: () => {
      toast({
        variant: 'destructive',
        description: 'Παρουσιάστηκε κάποιο πρόβλημα',
      });
    },
    enabled: false,
  });
};

export default useDriversData;
